import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Map as MapIcon,
  Pets as PetsIcon,
  AddAPhoto as AddPhotoIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../services/api';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };

  const navItems = [
    { label: 'Map', path: '/', icon: <MapIcon /> },
    { label: 'Cats', path: '/cats', icon: <PetsIcon /> },
  ];

  const authNavItems = [
    { label: 'Upload Cat', path: '/upload', icon: <AddPhotoIcon /> },
  ];

  const DrawerContent = () => (
    <Box
      sx={{ width: 280, pt: 2 }}
      role="presentation"
      onClick={() => setDrawerOpen(false)}
    >
      <Box sx={{ px: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <PetsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight="bold">
          Catlas
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.path}
            component={RouterLink}
            to={item.path}
            sx={{
              '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.1)' },
              borderRadius: 2,
              mx: 1,
            }}
          >
            <ListItemIcon sx={{ color: 'primary.light' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        {user && authNavItems.map((item) => (
          <ListItem
            key={item.path}
            component={RouterLink}
            to={item.path}
            sx={{
              '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.1)' },
              borderRadius: 2,
              mx: 1,
            }}
          >
            <ListItemIcon sx={{ color: 'primary.light' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1 }} />
      <List>
        {user ? (
          <>
            <ListItem
              component={RouterLink}
              to="/profile"
              sx={{
                '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.1)' },
                borderRadius: 2,
                mx: 1,
              }}
            >
              <ListItemIcon>
                <Avatar
                  src={getImageUrl(user.profilePicture)}
                  sx={{ width: 32, height: 32 }}
                >
                  {user.username[0].toUpperCase()}
                </Avatar>
              </ListItemIcon>
              <ListItemText primary={user.username} secondary="View Profile" />
            </ListItem>
            {user.isAdmin && (
              <ListItem
                component={RouterLink}
                to="/admin"
                sx={{
                  '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.1)' },
                  borderRadius: 2,
                  mx: 1,
                }}
              >
                <ListItemIcon sx={{ color: 'warning.main' }}>
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText primary="Admin Dashboard" />
              </ListItem>
            )}
            <ListItem
              onClick={handleLogout}
              sx={{
                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' },
                borderRadius: 2,
                mx: 1,
                cursor: 'pointer',
              }}
            >
              <ListItemIcon sx={{ color: 'error.main' }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem
              component={RouterLink}
              to="/login"
              sx={{
                '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.1)' },
                borderRadius: 2,
                mx: 1,
              }}
            >
              <ListItemIcon sx={{ color: 'primary.light' }}>
                <LoginIcon />
              </ListItemIcon>
              <ListItemText primary="Login" />
            </ListItem>
            <ListItem
              component={RouterLink}
              to="/register"
              sx={{
                '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.1)' },
                borderRadius: 2,
                mx: 1,
              }}
            >
              <ListItemIcon sx={{ color: 'secondary.main' }}>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Register" />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar position="sticky">
      <Toolbar>
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setDrawerOpen(true)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <PetsIcon sx={{ fontSize: 28, color: 'primary.light' }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #a78bfa 0%, #67e8f9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Catlas
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            {navItems.map((item) => (
              <Button
                key={item.path}
                component={RouterLink}
                to={item.path}
                color="inherit"
                startIcon={item.icon}
              >
                {item.label}
              </Button>
            ))}
            {user && authNavItems.map((item) => (
              <Button
                key={item.path}
                component={RouterLink}
                to={item.path}
                color="inherit"
                startIcon={item.icon}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        )}

        {!isMobile && (
          user ? (
            <>
              <IconButton onClick={handleMenuOpen} sx={{ p: 0.5 }}>
                <Avatar
                  src={getImageUrl(user.profilePicture)}
                  sx={{
                    width: 36,
                    height: 36,
                    border: '2px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  {user.username[0].toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 180,
                    background: 'linear-gradient(145deg, #1a1a2e 0%, #16162a 100%)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  },
                }}
              >
                <MenuItem
                  component={RouterLink}
                  to="/profile"
                  onClick={handleMenuClose}
                >
                  <PersonIcon sx={{ mr: 1.5, fontSize: 20 }} /> Profile
                </MenuItem>
                {user.isAdmin && (
                  <MenuItem
                    component={RouterLink}
                    to="/admin"
                    onClick={handleMenuClose}
                  >
                    <AdminIcon sx={{ mr: 1.5, fontSize: 20, color: 'warning.main' }} /> Admin
                  </MenuItem>
                )}
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} /> Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                component={RouterLink}
                to="/login"
                variant="outlined"
                size="small"
              >
                Login
              </Button>
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="small"
              >
                Register
              </Button>
            </Box>
          )
        )}

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              background: 'linear-gradient(145deg, #1a1a2e 0%, #0f0f1a 100%)',
              borderRight: '1px solid rgba(255,255,255,0.1)',
            },
          }}
        >
          <DrawerContent />
        </Drawer>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
